import { GoalProgressDateRangePreset, GoalType } from '@griply/types';
import { getGoalProgressDateRange } from '@griply/utils';
import { useGoalById } from 'features/goals';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { GoalInsightsCard } from 'shared/components/connected/goal-insights-card';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { EmptyChart } from './components/empty-chart';
import { ProgressOverview } from './components/progress-overview';
import { useGoalProgressUpdate } from './hooks/use-goal-progress-update';
import { useTimeframeTimes } from './hooks/use-timeframe-times';

export const Progress: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { id } = useParams();
  const goal = useGoalById(id);
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const { canUpdate, onUpdate } = useGoalProgressUpdate(goal);

  const goalDateRange = useTimeframeTimes(goal);
  const preset =
    goal?.progressDateRangePreset ?? GoalProgressDateRangePreset.Goal;

  const dateRange = useMemo(
    () =>
      getGoalProgressDateRange(preset, {
        goalRange: goalDateRange,
        today,
        weekStartsOn,
      }),
    [preset, goalDateRange, today, weekStartsOn],
  );

  const dateRangeOptions = useMemo<Option<GoalProgressDateRangePreset>[][]>(
    () => [
      [
        {
          label: t('general.labels.goal-progress.date-range.all-time.short'),
          value: GoalProgressDateRangePreset.Goal,
          tooltip: t('general.labels.goal-progress.date-range.all-time.label'),
        },
        {
          label: t('general.labels.timeframes.week.short'),
          value: GoalProgressDateRangePreset.Week,
          tooltip: t('general.labels.goal-progress.date-range.this-week.label'),
        },
        {
          label: t('general.labels.timeframes.month.short'),
          value: GoalProgressDateRangePreset.Month,
          tooltip: t(
            'general.labels.goal-progress.date-range.this-month.label',
          ),
        },
        {
          label: t('general.labels.timeframes.quarter.short'),
          value: GoalProgressDateRangePreset.Quarter,
          tooltip: t(
            'general.labels.goal-progress.date-range.this-quarter.label',
          ),
        },
        {
          label: t('general.labels.timeframes.year.short'),
          value: GoalProgressDateRangePreset.Year,
          tooltip: t('general.labels.goal-progress.date-range.this-year.label'),
        },
      ],
    ],
    [t],
  );

  const isAllRange = preset === GoalProgressDateRangePreset.Goal;

  const handleChangePreset = (next: GoalProgressDateRangePreset) => {
    if (!id) {
      return;
    }
    updateGoal({ goal: { id, progressDateRangePreset: next } });
  };

  // Projects never show a progress card — they measure task completion, not a metric.
  if (!goal || goal.type === GoalType.Project) {
    return null;
  }

  return (
    <>
      <PageSectionBase
        title={t('pages.goal-detail.progress.main-target.title')}
        centerInteractions={
          <SliderButtons
            optionGroups={dateRangeOptions}
            onOption={handleChangePreset}
            value={preset}
            preventDrag
          />
        }
        interactions={
          canUpdate ? (
            <Button
              onClick={onUpdate}
              variant={Variants.ContainedLight}
              size={Sizes.Small}
              color={Colors.Inherit}
            >
              {t('general.labels.insights-card.update-target.short.label')}
            </Button>
          ) : undefined
        }
        noPadding
      >
        {!goal.metric?.id ? (
          <EmptyChart />
        ) : (
          <GoalInsightsCard
            goal={goal}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            hideTimeframeDifference={isAllRange}
            hideHeader
            showLeftAxis
          />
        )}
      </PageSectionBase>

      <ProgressOverview dateRange={dateRange} isAllRange={isAllRange} />
    </>
  );
};
